import { DeleteMemberDialog, InviteUserDialog, TenantMembersTable } from '@/components/tenant-members'
import { Pagination } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTenant } from '@/contexts/TenantContext'
import { useDebounce, usePagination, useRemoveTenantUser, useTenantMembers } from '@/hooks'
import { TENANT_MEMBER_DEFAULT_SORT_DIRECTIONS, type TenantMember, type TenantMemberSortColumn } from '@/types'
import { ClearFiltersIcon } from '@/components/icons'
import { Search, X } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

const SORT_STORAGE_KEY = 'tenant-members-sort'
const SORT_PARAM_KEYS = ['sortBy', 'sort'] as const
const DEFAULT_SORT_BY: TenantMemberSortColumn = 'userName'

export function TenantMembersPage() {
  const { currentTenant } = useTenant()
  const tenantId = currentTenant?.id
  const isTenantAdmin = currentTenant?.role === 'Admin'

  const { page, pageSize, paginationParams, setPage, setPageSize, resetPage } = usePagination()
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [memberToDelete, setMemberToDelete] = useState<TenantMember | null>(null)
  const removeUser = useRemoveTenantUser()
  const [searchParams, setSearchParams] = useSearchParams()

  const persistSort = useCallback((params: URLSearchParams) => {
    const sortParams = new URLSearchParams()
    SORT_PARAM_KEYS.forEach(k => {
      const v = params.get(k)
      if (v) sortParams.set(k, v)
    })
    sessionStorage.setItem(SORT_STORAGE_KEY, sortParams.toString())
  }, [])

  useLayoutEffect(() => {
    if (SORT_PARAM_KEYS.some(k => searchParams.has(k))) return
    const saved = sessionStorage.getItem(SORT_STORAGE_KEY)
    if (!saved) return
    const savedParams = new URLSearchParams(saved)
    if (!Array.from(savedParams.keys()).length) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      savedParams.forEach((value, key) => next.set(key, value))
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams])

  const sortByParam = searchParams.get('sortBy')
  const sortBy: TenantMemberSortColumn =
    sortByParam && Object.hasOwn(TENANT_MEMBER_DEFAULT_SORT_DIRECTIONS, sortByParam)
      ? (sortByParam as TenantMemberSortColumn)
      : DEFAULT_SORT_BY
  const sortDirParam = searchParams.get('sort')
  const sortDirection: 'asc' | 'desc' =
    sortDirParam === 'asc' || sortDirParam === 'desc' ? sortDirParam : TENANT_MEMBER_DEFAULT_SORT_DIRECTIONS[sortBy]
  const isDefaultSort =
    sortBy === DEFAULT_SORT_BY && sortDirection === TENANT_MEMBER_DEFAULT_SORT_DIRECTIONS[DEFAULT_SORT_BY]
  const sort = useMemo(() => ({ sortBy, sortDirection }), [sortBy, sortDirection])

  useEffect(() => {
    resetPage()
  }, [debouncedSearch, roleFilter, statusFilter, resetPage])

  const { data, isLoading } = useTenantMembers(
    tenantId,
    paginationParams,
    debouncedSearch,
    roleFilter || undefined,
    statusFilter || undefined,
    sort,
  )
  const members = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  const handleSortChange = (column: TenantMemberSortColumn) => {
    const nextDirection =
      column === sortBy ? (sortDirection === 'asc' ? 'desc' : 'asc') : TENANT_MEMBER_DEFAULT_SORT_DIRECTIONS[column]
    const next = new URLSearchParams(searchParams)
    next.set('sortBy', column)
    next.set('sort', nextDirection)
    persistSort(next)
    setSearchParams(next, { replace: true })
    resetPage()
  }

  const handleClearAll = () => {
    setSearchInput('')
    setRoleFilter('')
    setStatusFilter('')
    const next = new URLSearchParams(searchParams)
    SORT_PARAM_KEYS.forEach(k => next.delete(k))
    persistSort(next)
    setSearchParams(next, { replace: true })
  }

  if (!tenantId || !isTenantAdmin) {
    return <Navigate to="/projects" replace />
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">User Management</h1>
        <Button onClick={() => setInviteOpen(true)}>+ New User</Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Select
          value={roleFilter || 'all'}
          onValueChange={(val) => setRoleFilter(val === 'all' ? '' : val)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="Admin">Admin</SelectItem>
            <SelectItem value="Viewer">Viewer</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={statusFilter || 'all'}
          onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Invited">Invited</SelectItem>
          </SelectContent>
        </Select>

        <Button
          type="button"
          variant="ghost"
          onClick={handleClearAll}
          disabled={!searchInput && !roleFilter && !statusFilter && isDefaultSort}
          className="flex items-center gap-1.5 text-sm font-medium"
          aria-label="Clear all filters"
        >
          <ClearFiltersIcon className="h-4 w-4" />
          Clear All
        </Button>

      </div>

      <div className="rounded-md border">
        <TenantMembersTable
          members={members}
          isLoading={isLoading}
          onRemove={setMemberToDelete}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
        />
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {tenantId && (
        <InviteUserDialog
          defaultTenantId={tenantId}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      )}

      <DeleteMemberDialog
        member={memberToDelete}
        open={memberToDelete !== null}
        onOpenChange={(open) => { if (!open && !removeUser.isPending) setMemberToDelete(null) }}
        onConfirm={async () => {
          if (!tenantId || !memberToDelete) return
          try {
            await removeUser.mutateAsync({ tenantId, userId: memberToDelete.id })
            toast.success('User deleted successfully')
            setMemberToDelete(null)
          } catch {
            toast.error('Failed to delete user')
          }
        }}
        isDeleting={removeUser.isPending}
      />
    </div>
  )
}
